import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import constants from "../../../common/constants";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MobileVerificationModal from "../MobileVerificationModal/MobileVerificationModal";
import {
  mobileVerifyOtpRequestThunk,
  mobileVerifyOtpThunk,
} from "../../Register/reducer/abhaAadhar/thunk";
import Methods from "../../../utility/Methods";
import SnackbarComponent from "../../../components/snackbar/index.tsx";
import BackdropComponent from "../../../components/backdrop/index.tsx";

const ExistingAbhaProfile = ({ abhaProfile = {}, userMobile, txnId }) => {
  console.log(abhaProfile, "abhaProfile");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(true);
  const [showMobileVerification, setShowMobileVerification] = useState(false);
  const [verificationTxnId, setVerificationTxnId] = useState(txnId || "");

  const profileMobile = abhaProfile?.ABHAProfile?.mobile || "";
  const isMobileDifferent =
    userMobile && (!profileMobile || userMobile !== profileMobile);

  // Utility function to mask mobile number
  const maskMobile = (mobile) => {
    if (!mobile || mobile.length < 4) return mobile;
    const lastFour = mobile.slice(-4);
    const masked = "*".repeat(mobile.length - 4);
    return masked + lastFour;
  };

  const [snackbarOpen, setSnackbarOpen] = useState({
    open: false,
    severity: "",
    message: "",
  });

  const mobileVerifyOtpRequest = useSelector(
    (state) => state.abhaAddress.mobileVerifyOtpRequest
  );
  const {
    status: mobileVerifyOtpRequestStatus,
    data: mobileVerifyOtpRequestData,
    error: mobileVerifyOtpRequestError,
  } = mobileVerifyOtpRequest;

  const mobileVerifyOtp = useSelector(
    (state) => state.abhaAddress.mobileVerifyOtp
  );
  const {
    status: mobileVerifyOtpStatus,
    data: mobileVerifyOtpData,
    error: mobileVerifyOtpError,
  } = mobileVerifyOtp;

  useEffect(() => {
    if (isMobileDifferent) {
      // Request OTP for mobile verification
      const reqObj = {
        loginId: userMobile,
        loginHint: "mobile",
        scope: "mobile-verify",
        txnId: verificationTxnId,
      };
      let finalReqObj = Methods.encryptObject(reqObj);
      dispatch(mobileVerifyOtpRequestThunk(finalReqObj));
    }
  }, []);

  useEffect(() => {
    if (mobileVerifyOtpRequestStatus === "succeeded") {
      let decData = JSON.parse(
        Methods.decryptData(mobileVerifyOtpRequestData?.data)
      );
      let finalData = decData.response;
      setVerificationTxnId(finalData?.txnId);
      setShowMobileVerification(true);
      setOpen(false);
    } else if (mobileVerifyOtpRequestStatus === "failed") {
      let decData =
        mobileVerifyOtpRequestError?.data &&
        JSON.parse(Methods.decryptData(mobileVerifyOtpRequestError?.data));
      handleShowSnackbar("error", decData?.error?.errorMessage);
    }
  }, [mobileVerifyOtpRequestStatus]);

  useEffect(() => {
    if (mobileVerifyOtpStatus === "succeeded") {
      let decData = JSON.parse(Methods.decryptData(mobileVerifyOtpData?.data));
      let finalData = decData.response;

      // Check authResult field to determine actual success/failure
      // API returns 200 even for wrong OTP, so we need to check authResult
      if (finalData?.authResult === "failed") {
        handleShowSnackbar(
          "error",
          finalData?.message || "OTP verification failed"
        );
      } else if (finalData?.authResult === "success") {
        // Store tokens and login
        if (finalData?.tokens) {
          sessionStorage.setItem("accessToken", finalData.tokens.token);
          sessionStorage.setItem("refreshToken", finalData.tokens.refreshToken);
        }

        handleShowSnackbar("success", "Mobile number verified successfully!");
        setTimeout(() => {
          navigate(constants.PATH_ABHA_PROFILE);
        }, 1500);
      }
    } else if (mobileVerifyOtpStatus === "failed") {
      let decData =
        mobileVerifyOtpError?.data &&
        JSON.parse(Methods.decryptData(mobileVerifyOtpError?.data));
      handleShowSnackbar("error", decData?.error?.errorMessage);
    }
  }, [mobileVerifyOtpStatus]);

  const handleShowSnackbar = (severity, message) => {
    setSnackbarOpen({
      open: true,
      severity,
      message,
    });
  };

  const handleSnackClose = () => {
    setSnackbarOpen({
      open: false,
      severity: "",
      message: "",
    });
  };

  const handleClose = () => {
    setOpen(false);
    setShowMobileVerification(false);
  };

  const handleViewProfile = () => {
    navigate(constants.PATH_ABHA_PROFILE);
  };

  const handleVerifyOtp = (otp) => {
    const reqObj = {
      otp: otp,
      txnId: verificationTxnId,
      scope: "mobile-verify",
    };
    let finalReqObj = Methods.encryptObject(reqObj);
    dispatch(mobileVerifyOtpThunk(finalReqObj));
  };

  const handleResendOtp = () => {
    const reqObj = {
      loginId: userMobile,
      loginHint: "mobile",
      scope: "mobile-verify",
      txnId: verificationTxnId,
    };
    let finalReqObj = Methods.encryptObject(reqObj);
    dispatch(mobileVerifyOtpRequestThunk(finalReqObj));
  };

  const handleSkipVerification = () => {
    setShowMobileVerification(false);
    navigate(constants.PATH_LOGIN);
  };

  return (
    <>
      {(mobileVerifyOtpRequestStatus === "pending" ||
        mobileVerifyOtpStatus === "pending") && <BackdropComponent />}
      <SnackbarComponent
        message={snackbarOpen?.message}
        isOpen={snackbarOpen?.open}
        purpose={snackbarOpen?.severity}
        handleClick={handleSnackClose}
      />

      {showMobileVerification ? (
        <MobileVerificationModal
          open={showMobileVerification}
          onClose={handleSkipVerification}
          onVerify={handleVerifyOtp}
          onResend={handleResendOtp}
          userMobile={userMobile}
          profileMobile={profileMobile}
        />
      ) : (
        <Dialog
          open={open}
          onClose={(event, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          sx={{ padding: "1.25rem" }}
        >
          <DialogTitle>
            <Box
              display="flex"
              flexDirection={"column"}
              alignItems="center"
              gap={1}
            >
              <InfoOutlinedIcon
                color="primary"
                sx={{ color: "#f5841f", height: "3rem", width: "3rem" }}
              />
              <Typography
                fontSize={"1.5rem"}
                fontWeight={"600"}
                color="#f5841f"
              >
                ABHA Account Exist
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent>
            <DialogContentText>
              We have found an ABHA against the Aadhaar number provided by you.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleViewProfile}
              fullWidth
              color="primary"
              variant="contained"
              className="abha_flow_btn"
            >
              View Profile
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default ExistingAbhaProfile;
